/**
 * @fileoverview Task Configuration Converters
 *
 * This module contains converters for transforming task configurations between
 * different formats (frontend UI state, stored automations, backend API payloads).
 *
 * KEY CONCEPTS:
 * - taskConfig: The UI state representation that the hook manages
 * - task: Backend task object from the database
 * - payload: API request format for backend submission
 *
 * CONVERTER TYPES:
 * 1. Task Loaders: Convert backend task to taskConfig for editing
 * 2. Payload Builders: Convert taskConfig to backend API format
 * 3. Normalization Utilities: Standardize selectors and arrays
 */

import { CONFIGURATIONS } from "../config/configurations";
import { CATALOGUE_TASK_TYPES } from "../config/automationTypes";

// ============================================================================
// CONSTANTS
// ============================================================================

export const SUPPORTED_AUTOMATION_TYPES = ["listExtractor", "pageExtractor"];

// Map backend task types to frontend automation types
const BACKEND_TO_FRONTEND_TYPE = {
  extract_list: "listExtractor",
  extract_page_details: "pageExtractor",
  extract_pages: "pageExtractor",
  extract_google_maps_pages: "pageExtractor",
  extract_emails: "emailExtractor",
  extract_social_links: "socialLinkExtractor",
  extract_shopify: "shopifyExtractor",
  extract_woocommerce: "wooCommerceExtractor",
  extract_magento: "magentoExtractor",
  extract_salesforce_commerce: "salesforceCommerceExtractor",
};

// Map frontend automation types to backend task types
const FRONTEND_TO_BACKEND_TYPE = {
  listExtractor: "extract_list",
  pageExtractor: "extract_page_details",
  emailExtractor: "extract_emails",
  socialLinkExtractor: "extract_social_links",
  shopifyExtractor: "extract_shopify",
  wooCommerceExtractor: "extract_woocommerce",
  magentoExtractor: "extract_magento",
  salesforceCommerceExtractor: "extract_salesforce_commerce",
};

// ============================================================================
// TASK TO CONFIG CONVERTERS
// These functions convert backend task format to UI taskConfig format
// ============================================================================

/**
 * Builds a simple taskConfig from the backend task for UI editing.
 * The task from the API already has a well-structured format, so we just
 * flatten the configuration array into an object keyed by config ID.
 *
 * @param {Object} task - Backend task object
 * @returns {Object} taskConfig for UI state
 */
export const convertTaskToTaskConfig = (task) => {
  if (!task) return null;

  console.log("🔄 [convertTaskToTaskConfig] Input task:", {
    id: task.id,
    task_type: task.task_type,
    hasRequest: !!task.request,
    automationUrl: task.request?.automation?.url,
    configCount: task.request?.configuration?.length,
  });

  const automationType = BACKEND_TO_FRONTEND_TYPE[task.task_type] || "listExtractor";

  // Build taskConfig - mostly just reorganizing the task data
  const taskConfig = {
    automationType,
    metadata: {
      title: task.name || task.request?.metadata?.title || "",
      websiteUrl: task.request?.metadata?.url || "",
      websiteFavicon: task.request?.metadata?.favicon || "",
    },
    // Keep automation object as-is from the task, and restore extractionMode for Google Maps tasks
    automation: {
      ...(task.request?.automation || {}),
      ...(task.task_type === "extract_google_maps_pages" ? { extractionMode: "google_maps" } : {}),
    },
    // General config for task name and metadata display
    general: {
      name: task.name || "",
      websiteUrl: task.request?.metadata?.url || "",
      createdAt: task.created_at || null,
      updatedAt: task.updated_at || null,
    },
  };

  console.log("🔄 [convertTaskToTaskConfig] Initial taskConfig.automation:", taskConfig.automation);

  // Flatten configuration array into object keyed by ID
  // e.g., [{id: "proxyConfiguration", config: {...}}] => {proxyConfiguration: {...}}
  const configArray = task.request?.configuration || [];
  configArray.forEach((item) => {
    if (item.id && item.config) {
      console.log(`🔄 [convertTaskToTaskConfig] Adding config "${item.id}":`, item.config);
      taskConfig[item.id] = item.config;
    }
  });

  // Fill in defaults only for configs that don't exist at all
  CONFIGURATIONS.forEach((configDef) => {
    if (taskConfig[configDef.id] === undefined) {
      console.log(`🔄 [convertTaskToTaskConfig] Using default for "${configDef.id}"`);
      taskConfig[configDef.id] = configDef.defaultConfig ? { ...configDef.defaultConfig } : {};
    }
  });

  console.log("🔄 [convertTaskToTaskConfig] Final taskConfig keys:", Object.keys(taskConfig));

  return taskConfig;
};

// ============================================================================
// CONFIG TO PAYLOAD CONVERTERS
// These functions convert UI taskConfig to backend API format
// ============================================================================

/**
 * Converts a task configuration to a backend API PATCH payload for updates
 * This is used when saving changes in AutomationEditModal
 *
 * @param {Object} taskConfig - UI task configuration state
 * @returns {Object} PATCH payload for updateTask API
 */
export const convertTaskConfigToUpdates = (taskConfig) => {
  if (!taskConfig) {
    throw new Error("Task configuration is required to build the update payload");
  }

  if (!taskConfig.automationType) {
    throw new Error("Automation type is required");
  }

  // Map frontend automation type to backend type
  let taskType = FRONTEND_TO_BACKEND_TYPE[taskConfig.automationType] || "extract_list";

  // Page Extractor 2.0: when the automation carries an ordered steps[] list, route to the
  // steps-based type. Takes precedence over legacy extract_page_details, but NOT over the
  // Google Maps override below (steps first, then google_maps wins).
  const hasSteps = Array.isArray(taskConfig.automation?.steps) && taskConfig.automation.steps.length > 0;
  if (taskConfig.automationType === "pageExtractor" && hasSteps) {
    taskType = "extract_pages";
  }

  // Override for Google Maps extraction mode
  const extractionMode = taskConfig.automation?.extractionMode;
  if (taskConfig.automationType === "pageExtractor" && extractionMode === "google_maps") {
    taskType = "extract_google_maps_pages";
  }

  // Build automation payload based on type
  let automation;
  if (taskType === "extract_list") {
    const paginationMethod = taskConfig.automation?.paginationMethod || "none";

    automation = {
      type: "extract_list",
      url: taskConfig.automation?.url || taskConfig.urls?.[0] || "",
      listSelectors: normalizeSelectors(taskConfig.automation?.listSelectors),
      paginationMethod,
      paginationSelectors: normalizeSelectors(taskConfig.automation?.paginationSelectors),
    };

    // Only include automationOptions if they're explicitly set
    if (taskConfig.automation?.automationOptions) {
      automation.automationOptions = taskConfig.automation.automationOptions;
    }
  } else if (taskType === "extract_google_maps_pages") {
    const urls = taskConfig.automation?.urls?.length > 0
      ? taskConfig.automation.urls
      : taskConfig.urls || [];

    automation = {
      type: "extract_google_maps_pages",
      urls,
      extractionOptions: taskConfig.automation?.extractionOptions || {},
    };
  } else if (taskType === "extract_pages") {
    // Page Extractor 2.0: ordered steps (element + action steps). Normalize selectors inside
    // element steps; pass action steps through with their options.
    const urls = taskConfig.automation?.urls?.length > 0
      ? taskConfig.automation.urls
      : taskConfig.urls || [];

    const steps = toArray(taskConfig.automation?.steps)
      .map((step) => {
        if (step?.kind === "elements") {
          return {
            kind: "elements",
            elements: toArray(step.elements).map((el) => ({ ...el, selectors: normalizeSelectors(el?.selectors) })),
          };
        }
        if (step?.kind === "action") {
          return { kind: "action", actionId: step.actionId, options: step.options || {} };
        }
        return null;
      })
      .filter(Boolean);

    automation = { type: "extract_pages", urls, steps };
  } else if (taskType === "extract_page_details") {
    const urls = taskConfig.automation?.urls?.length > 0
      ? taskConfig.automation.urls
      : taskConfig.urls || [];

    const elements = toArray(taskConfig.automation?.elements).map((element) => ({
      ...element,
      selectors: normalizeSelectors(element?.selectors),
    }));

    automation = {
      type: "extract_page_details",
      urls,
      elements,
    };
  } else if (taskType === "extract_emails" || taskType === "extract_social_links") {
    const urls = taskConfig.automation?.urls?.length > 0
      ? taskConfig.automation.urls
      : taskConfig.urls || [];

    automation = {
      type: taskType,
      urls,
      config: taskConfig.automation?.config || {},
    };
  } else if (CATALOGUE_TASK_TYPES.has(taskType)) {
    // Catalogue automations are authored by the MCP agent via their platform service (store
    // detection, collection resolution, method selection). The dashboard has no editor for those
    // shapes, and the backend rejects an automation-type change on update — so we deliberately
    // leave `automation` undefined and never send it. The stored automation stays intact; only the
    // name and metadata are editable from the dashboard (the config panels are all excluded for
    // these types via their automationTypes allowlists, so `configuration` below resolves to an
    // empty array).
    automation = undefined;
  } else {
    throw new Error(`Unsupported automation type: ${taskConfig.automationType}`);
  }

  // Build configuration array from CONFIGURATIONS registry.
  // Respects the per-config automationTypes allowlist so configs hidden in the
  // UI for this type are not sent in the payload either.
  const configuration = CONFIGURATIONS
    .filter((configDef) => !configDef.disabled)
    .filter((configDef) => configDef.id !== "automation" && configDef.id !== "general")
    .filter((configDef) => !configDef.automationTypes || configDef.automationTypes.includes(taskConfig.automationType))
    .map((configDef) => ({
      id: configDef.id,
      category: configDef.category,
      config: taskConfig[configDef.id] || configDef.defaultConfig,
    }));

  // Get task name from general config (preferred) or fall back to metadata.title
  const taskName = taskConfig.general?.name || taskConfig.metadata?.title;

  // Build update payload - fields must be at top level (not wrapped in request)
  const updates = {
    name: taskName,
    configuration,
    metadata: {
      url: taskConfig.metadata?.websiteUrl,
      title: taskConfig.metadata?.title,
      favicon: taskConfig.metadata?.websiteFavicon,
    },
  };

  // Only send `automation` when we actually built one. Catalogue types leave it undefined so the
  // backend keeps the agent-authored automation as-is (PATCH treats automation as optional/partial).
  if (automation) {
    updates.automation = automation;
  }

  return updates;
};

// ============================================================================
// AUTOMATION LOADERS (for compatibility with existing code)
// These functions extract automation-specific config from stored automations
// ============================================================================

/**
 * Extracts list automation configuration from a stored automation object
 * Returns only the automation-specific fields needed for the UI
 */
export const loadListAutomationConfig = (automation) => {
  const actions = toArray(automation?.config?.actions);
  const extractAction = actions.find((action) => action?.type === "EXTRACT_LIST") || {};
  const loadMoreAction = actions.find((action) => action?.type === "LOAD_MORE") || {};

  const listSelectors = normalizeSelectors(extractAction.allSelectors);
  const loadMoreSelectors = normalizeSelectors(loadMoreAction.buttonSelectors);
  const paginationSelectors = loadMoreSelectors.length > 0 ? loadMoreSelectors : normalizeSelectors(extractAction.paginationSelectors);

  return {
    url: automation?.metadata?.url || "",
    listSelectors,
    paginationMethod: loadMoreAction.method || "none",
    paginationSelectors,
  };
};

/**
 * Extracts page automation configuration from a stored automation object
 * Returns only the automation-specific fields needed for the UI
 */
export const loadPageAutomationConfig = (automation) => {
  const actions = toArray(automation?.config?.actions);
  const extractAction = actions.find((action) => action?.type && action.type.startsWith("EXTRACT_PAGES")) || {};

  // Determine extraction mode from action type
  const extractionMode = extractAction.type === "EXTRACT_PAGES_GOOGLE_MAPS" ? "google_maps" : "regular";
  const extractionOptions = extractAction.config?.extractionOptions || null;

  return {
    urls: toArray(extractAction?.urls)
      .map((url) => (typeof url === "string" ? url.trim() : ""))
      .filter(Boolean),
    elements: toArray(extractAction?.elements).map((element) => ({
      ...element,
      selectors: normalizeSelectors(element?.selectors),
    })),
    extractionMode,
    extractionOptions,
  };
};

/**
 * Loads automation config into a task config
 * This merges the automation-specific fields into the default config
 */
export const loadAutomationIntoTaskConfig = (taskConfig, automation) => {
  if (!automation) return taskConfig;

  const updatedConfig = { ...taskConfig };

  // Update automation type
  updatedConfig.automationType = automation.type || taskConfig.automationType;

  // Update metadata
  updatedConfig.metadata = {
    title: automation?.metadata?.title || taskConfig.metadata.title,
    websiteUrl: automation?.metadata?.url || taskConfig.metadata.websiteUrl,
    websiteFavicon: automation?.metadata?.favicon || taskConfig.metadata.websiteFavicon,
  };

  // Load automation-specific configuration
  if (automation.type === "listExtractor") {
    const automationSection = loadListAutomationConfig(automation);
    updatedConfig.automation = automationSection;
    // For list extractors, urls is a single URL - store as array with one element
    updatedConfig.urls = automationSection.url ? [automationSection.url] : [];
  } else if (automation.type === "pageExtractor") {
    const automationSection = loadPageAutomationConfig(automation);
    updatedConfig.automation = automationSection;
    // For page extractors, urls is already an array
    updatedConfig.urls = automationSection.urls || [];
  }

  return updatedConfig;
};

// ============================================================================
// NORMALIZATION UTILITIES
// ============================================================================

const toArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const normalizeSelector = (selector) => {
  if (!selector) return null;
  if (typeof selector === "string") {
    return { type: "css", value: selector };
  }

  const value = selector.value || selector.selector || selector.xpath || "";
  if (!value) return null;

  // Map selector types to backend-compatible values
  let type = selector.type || (selector.xpath ? "xpath" : "css");
  if (type === "selector") {
    type = "css";
  }

  return {
    type,
    value,
  };
};

const normalizeSelectors = (selectors) => toArray(selectors).map(normalizeSelector).filter(Boolean);

// ============================================================================
// CREATE PAYLOAD BUILDER
// ============================================================================

/**
 * Converts a task configuration to a backend API POST payload for creation.
 * Reuses convertTaskConfigToUpdates for the core fields, then adds
 * the taskType and optional flags needed by the create endpoint.
 *
 * @param {Object} taskConfig - UI task configuration state
 * @param {Object} options
 * @param {boolean} [options.runImmediately=false] - Queue task for immediate run after creation
 * @returns {Object} POST payload for createTask API
 */
export const convertTaskConfigToCreatePayload = (taskConfig, { runImmediately = false } = {}) => {
  // Get the core payload (name, automation, configuration, metadata)
  const updates = convertTaskConfigToUpdates(taskConfig);

  // Derive taskType — must match the automation.type set by convertTaskConfigToUpdates
  let taskType = FRONTEND_TO_BACKEND_TYPE[taskConfig.automationType] || "extract_list";
  const hasSteps = Array.isArray(taskConfig.automation?.steps) && taskConfig.automation.steps.length > 0;
  if (taskConfig.automationType === "pageExtractor" && hasSteps) {
    taskType = "extract_pages";
  }
  if (taskConfig.automationType === "pageExtractor" && taskConfig.automation?.extractionMode === "google_maps") {
    taskType = "extract_google_maps_pages";
  }

  return {
    ...updates,
    taskType,
    runImmediately,
  };
};
